/**
 * Map FH custom field type to booking field type
 */
export function mapFHTypeToBookingFieldType(fhType) {
    switch (fhType) {
        case 'extended-option':
            return 'select';
        case 'yes-no':
            return 'boolean';
        case 'count':
        case 'small-number':
        case 'large-number':
            return 'number';
        case 'new-transportation':
            return 'lodging';
        case 'short':
        case 'long':
        default:
            return 'string';
    }
}

/**
 * Create a booking field from FH custom field
 */
export function createBookingFieldFromFH(field) {
    const bookingFieldType = mapFHTypeToBookingFieldType(field.type);

    const bookingField = {
        id: String(field.instance_id),
        name: field.title || `FH Field ${field.instance_id}`,
        type: bookingFieldType,
        required: true,
        privateField: field.is_private || false,
        agentOnly: false,
        readOnly: field.is_display_only || false,
        bookerOnly: 'booker',
        fhFieldPk: field.instance_id
    };

    // For extended-option, create select options with fhOptionPk mappings
    if (field.type === 'extended-option' && field.extendedOptions?.length > 0) {
        bookingField.options = field.extendedOptions
            .filter(opt => !opt.is_archived)
            .map(opt => ({
                label: opt.name,
                fhOptionPk: String(opt.pk)
            }));
        if (bookingField.options.length > 0) {
            bookingField.defaultValue = 0;
        }
    }

    // For yes-no (boolean), default to false
    if (field.type === 'yes-no') {
        bookingField.defaultValue = false;
    }

    return bookingField;
}

/**
 * Build existingCustomFields object from a ticket's booking_fields
 * (same logic as FareharborDirectIntegrationInfo handleTestBooking)
 */
export function buildExistingCustomFields(bookingFields) {
    const existingCustomFields = {};
    (bookingFields || [])
        .filter(f => f.fhFieldPk)
        .forEach(f => {
            if (f.type === 'select' && f.options?.length > 0) {
                const firstOption = f.options[0];
                if (firstOption.fhOptionPk) {
                    existingCustomFields[f.fhFieldPk] = parseInt(firstOption.fhOptionPk, 10);
                }
            } else if (f.type === 'boolean') {
                existingCustomFields[f.fhFieldPk] = false;
            } else if (f.type === 'lodging') {
                // Skip lodging for test bookings
            } else {
                existingCustomFields[f.fhFieldPk] = 'Test';
            }
        });
    return existingCustomFields;
}

/** Supported FH field types for auto-creation */
export const SUPPORTED_FH_FIELD_TYPES = [
    'extended-option',
    'yes-no',
    'short',
    'long',
    'count',
    'small-number',
    'large-number',
    'new-transportation'
];

/**
 * Convert missing fields from testBookingFields result into booking field objects
 */
export function convertMissingFieldsToBookingFields(missingFields, existingFieldPks) {
    return missingFields
        .filter(
            f =>
                !existingFieldPks.has(f.fieldId) &&
                !f.isDisplayOnly &&
                SUPPORTED_FH_FIELD_TYPES.includes(f.type)
        )
        .map(f =>
            createBookingFieldFromFH({
                instance_id: f.fieldId,
                title: f.title,
                type: f.type,
                is_private: f.isPrivate,
                is_display_only: f.isDisplayOnly,
                extendedOptions: f.extendedOptions || []
            })
        );
}
